import { fetchSvgText, inlineSvg, packUrl } from './svg.js';

/**
 * Icons: the game's entire vocabulary.
 *
 * She cannot read, so every goal, reward and destination is communicated by one of these. That makes
 * them load-bearing rather than decorative, which is why they are authored SVGs in the content pack
 * and not emoji: emoji render differently on Android and iOS, at different optical sizes, and a couple
 * of them are unrecognisable at small sizes on one platform or the other.
 *
 * Fetched once and cached: the same icon appears in the briefing, the HUD, the ribbon and the reward
 * screen, and it must not be re-fetched for each.
 */

export interface ItemDef {
  readonly id: string;
  /** Parent-facing only. Never shown to the player. */
  readonly label: string;
}

/** Everything she can be asked to fetch. */
export const ITEMS: readonly ItemDef[] = [
  { id: 'ball', label: 'ball' },
  { id: 'bone', label: 'bone' },
  { id: 'treat', label: 'dog treat' },
  { id: 'frisbee', label: 'frisbee' },
  { id: 'hydrant', label: 'fire hydrant' },
  { id: 'toolbox', label: 'toolbox' },
];

/** The Lookout: the goal on every return leg. */
export const LOOKOUT_ICON = 'lookout';

export function itemById(id: string | null): ItemDef | null {
  if (id === null) return null;
  return ITEMS.find((item) => item.id === id) ?? null;
}

function iconUrl(id: string): string {
  return packUrl('items', id);
}

/**
 * The raw source, for the bake. `src/content/svg.ts` owns the fetch and its cache.
 *
 * The Pixi texture built from it lives in `src/content/icon-texture.ts` - see the note there. This module
 * stays free of the renderer so the DOM screens can have their icons without it.
 */
export function loadIconSvg(id: string): Promise<string> {
  return fetchSvgText(iconUrl(id));
}

/** An inline SVG element, for the DOM screens. */
export function iconElement(id: string): Promise<SVGSVGElement | null> {
  return inlineSvg(iconUrl(id));
}

